import React from 'react';
import classNames from 'classnames/bind';
import { Input } from '@mantine/core';
import { useCombobox, UseComboboxStateChange } from 'downshift';
import { UseQueryResult } from '@tanstack/react-query';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

import APIError from 'types/api_error';
import { OffsetPaginated } from 'types/pagination';

import { Icon } from 'components/Icon/IconDeprecated';

import { QuerySearchProps, SearchableEntity } from './QuerySearch';
import { QuerySearchSuggestions } from './Suggestions';
import styles from './QuerySearchInput.module.scss';

const c = classNames.bind(styles);

type QuerySearchInputProps<T extends SearchableEntity> = Omit<
  QuerySearchProps<T>,
  'entityName' | 'totalElements' | 'filters'
>;

export function QuerySearchInput<T extends SearchableEntity>({
  value,
  searchLabel = 'Search...',
  disabled,
  searchResultField,
  searchQueryResult,
  onApplyFilters,
  onSuggestionSelect,
  onValueChange,
}: QuerySearchInputProps<T>) {
  if (searchQueryResult && searchResultField) {
    return (
      <QuerySearchInputWithSuggestions
        value={value}
        searchQueryResult={searchQueryResult}
        searchResultField={searchResultField}
        searchLabel={searchLabel}
        disabled={disabled}
        onApplyFilters={onApplyFilters}
        onValueChange={onValueChange}
        onSuggestionSelect={onSuggestionSelect}
      />
    );
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      onApplyFilters?.();
    }
  }

  return (
    <div className={c('combobox')}>
      <Input
        aria-label={searchLabel}
        value={value}
        placeholder={searchLabel}
        disabled={disabled}
        onChange={(event) => onValueChange(event.target.value)}
        onKeyDown={handleKeyDown}
        leftSection={<Icon name="search" />}
        variant="search"
        type="search"
        autoComplete="off"
        autoCorrect="off"
      />
    </div>
  );
}

type QuerySearchInputWithSuggestionsProps<T extends SearchableEntity> = Omit<
  QuerySearchProps<T>,
  | 'entityName'
  | 'totalElements'
  | 'filters'
  | 'searchQueryResult'
  | 'searchResultField'
> & {
  searchResultField: keyof T;
  searchQueryResult: UseQueryResult<OffsetPaginated<T>, APIError>;
};

function QuerySearchInputWithSuggestions<T extends SearchableEntity>({
  value,
  searchLabel = 'Search...',
  disabled,
  searchResultField,
  searchQueryResult,
  onApplyFilters,
  onSuggestionSelect,
  onValueChange,
}: QuerySearchInputWithSuggestionsProps<T>) {
  const items = searchQueryResult.data?.data || [];

  function handleValueChange({ inputValue }: UseComboboxStateChange<T | null>) {
    onValueChange(inputValue);
  }

  const {
    isOpen,
    highlightedIndex,
    inputValue,
    getLabelProps,
    getInputProps,
    getMenuProps,
    getItemProps,
    closeMenu,
  } = useCombobox<T | null>({
    // null is appended to keep the "ALl results for..." option accessible
    items: [...items, null],
    itemToString,
    onSelectedItemChange: handleItemSelect,
    onInputValueChange: handleValueChange,
  });

  function itemToString(item: T | null) {
    return item &&
      searchResultField in item &&
      typeof item[searchResultField] === 'string'
      ? String(item[searchResultField])
      : inputValue;
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' && highlightedIndex === -1) {
      onApplyFilters?.();
      closeMenu();
    }
  }

  function handleItemSelect({
    selectedItem,
  }: UseComboboxStateChange<T | null>) {
    if (!selectedItem) {
      return;
    }

    if (onSuggestionSelect) {
      onSuggestionSelect(selectedItem);
      return;
    }

    onApplyFilters?.();
    closeMenu();
  }

  return (
    <div className={c('combobox')}>
      <VisuallyHidden>
        <label {...getLabelProps()}>{searchLabel}</label>
      </VisuallyHidden>

      <Input
        {...getInputProps({ onKeyDown: handleKeyDown, value })}
        variant="search"
        leftSection={<Icon name="search" />}
        placeholder={searchLabel}
        disabled={disabled}
        type="search"
        autoComplete="off"
        autoCorrect="off"
        spellCheck="false"
      />

      <div
        {...getMenuProps()}
        className={c('suggestions', { open: isOpen && inputValue.trim() })}
      >
        {isOpen && (
          <QuerySearchSuggestions
            items={items}
            inputValue={inputValue}
            getItemProps={getItemProps}
            itemToString={itemToString}
            highlightedIndex={highlightedIndex}
            searchQueryResult={searchQueryResult}
            onApplyFilters={onApplyFilters}
            closeMenu={closeMenu}
          />
        )}
      </div>
    </div>
  );
}
